import { lazy } from "react";
import { Navigate, Route, useParams } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PlayerLayout } from "@/components/layouts/PlayerLayout";

/**
 * Le profil d'un autre joueur n'a plus qu'une adresse.
 *
 * Il en avait deux — `/players/:id` (page publique, partageable) et
 * `/player/profile/:id` (dans la coquille) — servies par deux composants différents,
 * donc deux dessins qui divergeaient déjà. `/players/:id` est celle qu'on partage : elle
 * l'emporte, et `PlayerProfileView` s'y adapte selon qu'on est connecté ou non.
 */
const RedirectToPlayerProfile = () => {
  const { id } = useParams();
  return <Navigate to={`/players/${id}`} replace />;
};

// Player pages
const ChampionshipDetailPlayer = lazy(() => import("@/pages/player/ChampionshipDetailPlayer"));
const ChampionshipRegistration = lazy(() => import("@/pages/player/ChampionshipRegistration"));
const TournamentsListPlayer = lazy(() => import("@/pages/player/TournamentsListPlayer"));
const TournamentDetailPlayer = lazy(() => import("@/pages/player/TournamentDetailPlayer"));
const TournamentRegistration = lazy(() => import("@/pages/player/TournamentRegistration"));
const RequestWildCard = lazy(() => import("@/pages/player/RequestWildCard"));
const MyReliability = lazy(() => import("@/pages/player/MyReliability"));
const MyTournamentDetail = lazy(() => import("@/pages/player/MyTournamentDetail"));
const ExploreMatches = lazy(() => import("@/pages/player/ExploreMatches"));
const CreateMatch = lazy(() => import("@/pages/player/CreateMatch"));
const CasualMatchDetail = lazy(() => import("@/pages/player/CasualMatchDetail"));
const JoinMatchByLink = lazy(() => import("@/pages/player/JoinMatchByLink"));
const Leaderboards = lazy(() => import("@/pages/player/Leaderboards"));
const PlayerProfile = lazy(() => import("@/pages/player/PlayerProfile"));
const PlayerStatsPage = lazy(() => import("@/pages/player/PlayerStatsPage"));
const Settings = lazy(() => import("@/pages/player/Settings"));
const TournamentHistory = lazy(() => import("@/pages/player/TournamentHistory"));
const Friends = lazy(() => import("@/pages/player/Friends"));
const BrowseLessons = lazy(() => import("@/pages/player/BrowseLessons"));
const BookLesson = lazy(() => import("@/pages/player/BookLesson"));
const MyLessons = lazy(() => import("@/pages/player/MyLessons"));
const FindCourtPage = lazy(() => import("@/pages/player/FindCourtPage"));
const CourtAgendaPage = lazy(() => import("@/pages/player/CourtAgendaPage"));
const CourtBookingDetailPage = lazy(() => import("@/pages/player/CourtBookingDetailPage"));
const MyAgendaPage = lazy(() => import("@/pages/player/MyAgendaPage"));
const ChatPage = lazy(() => import("@/pages/player/ChatPage"));
const MyCreditWallet = lazy(() => import("@/pages/player/MyCreditWallet"));
const BuyCreditPackage = lazy(() => import("@/pages/player/BuyCreditPackage"));
const OrganizationDetailPlayer = lazy(() => import("@/pages/player/OrganizationDetailPlayer"));
const MyClubs = lazy(() => import("@/pages/player/MyClubs"));
const OrganizationCourtBooking = lazy(() => import("@/pages/player/OrganizationCourtBooking"));
const SubmitScore = lazy(() => import("@/pages/player/SubmitScore"));
const MatchLobbyPage = lazy(() => import("@/pages/player/MatchLobbyPage"));
const ScoreEntryPage = lazy(() => import("@/components/matches/score/ScoreEntryPage"));
const ScoreReviewPage = lazy(() => import("@/components/matches/score/ScoreReviewPage"));
const EnrollGroupCourse = lazy(() => import("@/pages/player/EnrollGroupCourse"));
const PlayerSubscription = lazy(() => import("@/pages/player/PlayerSubscription"));

/**
 * Player-specific routes
 * All routes require player role and use PlayerLayout
 */
export const playerRoutes = (
  <Route
    path="/player"
    element={
      <ProtectedRoute requiredRoles={["player"]}>
        <PlayerLayout />
      </ProtectedRoute>
    }
  >
    {/* Tournaments */}
    <Route path="tournaments" element={<TournamentsListPlayer />} />
    <Route path="tournaments/:id" element={<TournamentDetailPlayer />} />
    <Route path="tournaments/:id/register" element={<TournamentRegistration />} />
    <Route path="championships/:id" element={<ChampionshipDetailPlayer />} />
    <Route path="championships/:id/register" element={<ChampionshipRegistration />} />
    <Route path="request-wildcard" element={<RequestWildCard />} />
    {/*
      « Mes tournois » était une page à part, avec ses propres cartes dessinées en ligne,
      différentes de celles de la liste. Elle devient un onglet de « Tournois » ; l'ancienne
      adresse, présente dans des notifications déjà envoyées, y mène.
    */}
    <Route path="my-tournaments" element={<Navigate to="/player/tournaments?tab=mine" replace />} />
    <Route path="my-tournaments/:id" element={<MyTournamentDetail />} />
    <Route path="tournament-history" element={<TournamentHistory />} />
    <Route path="reliability" element={<MyReliability />} />

    {/* Matches */}
    <Route path="matches" element={<ExploreMatches />} />
    {/*
      « Mes matchs » n'est plus une page : l'agenda porte déjà toutes mes échéances, et
      en tenir deux revenait à maintenir deux listes du même contenu. L'ancienne adresse
      ouvre l'agenda pré-filtré — les liens de notification déjà en base continuent donc
      de tomber au bon endroit.
    */}
    <Route path="my-matches" element={<Navigate to="/player/agenda?type=match" replace />} />
    <Route path="matches/create" element={<CreateMatch />} />
    <Route path="matches/:matchId" element={<CasualMatchDetail />} />
    <Route path="matches/:matchId/lobby" element={<MatchLobbyPage />} />
    <Route path="matches/:matchId/score" element={<ScoreEntryPage />} />
    <Route path="matches/:matchId/score/review" element={<ScoreReviewPage />} />
    <Route path="submit-score" element={<SubmitScore />} />
    <Route path="join-match/:token" element={<JoinMatchByLink />} />

    {/* Lessons */}
    <Route path="lessons" element={<BrowseLessons />} />
    <Route path="lessons/:lessonId/book" element={<BookLesson />} />
    <Route path="lessons/group/:courseId/enroll" element={<EnrollGroupCourse />} />
    <Route path="my-lessons" element={<MyLessons />} />

    {/* Courts & Bookings */}
    <Route path="find-court" element={<FindCourtPage />} />
    <Route path="bookings/:id" element={<CourtBookingDetailPage />} />

    {/* Credits */}
    <Route path="credits" element={<MyCreditWallet />} />
    <Route path="credits/buy" element={<BuyCreditPackage />} />

    {/* Organizations */}
    <Route path="clubs" element={<MyClubs />} />
    <Route path="organizations/:id" element={<OrganizationDetailPlayer />} />
    {/* L'agenda MONTRE les créneaux d'un club ou d'une asso ; `book-court` reste le
        chemin de DEMANDE d'un créneau d'association jusqu'au formulaire en trois
        étapes, qui réunira les deux. */}
    <Route path="organizations/:id/courts" element={<CourtAgendaPage />} />
    <Route path="organizations/:id/book-court" element={<OrganizationCourtBooking />} />

    {/* Social */}
    <Route path="friends" element={<Friends />} />
    <Route path="leaderboards" element={<Leaderboards />} />
    <Route path="chat" element={<ChatPage />} />

    {/* Profile & Settings */}
    <Route path="profile" element={<PlayerProfile />} />
    {/* Segment statique : React Router le classe avant `profile/:id`. */}
    <Route path="profile/stats" element={<PlayerStatsPage />} />
    <Route path="profile/:id" element={<RedirectToPlayerProfile />} />
    <Route path="settings" element={<Settings />} />
    <Route path="subscription" element={<PlayerSubscription />} />
    <Route path="agenda" element={<MyAgendaPage />} />
  </Route>
);
